import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ApiService } from 'src/app/shared/services/api.service';
import { LrsService, LrsRoute } from 'src/app/shared/services/lrs.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';
import { GisBoqGenerateDialogComponent } from '../../boq/gis-boq-generate-dialog/gis-boq-generate-dialog.component';

@Component({
  selector: 'app-road-boq-form',
  templateUrl: './road-boq-form.component.html',
  styleUrls: ['./road-boq-form.component.css'],
})
export class RoadBoqFormComponent implements OnInit {
  form!: FormGroup;
  recordId: number | null = null;
  loading = false;
  saving = false;
  lrsRoutes: LrsRoute[] = [];

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private lrsService: LrsService,
    private toastr: ToastrMsgService,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      boq_code: ['', Validators.required],
      boq_name: ['', Validators.required],
      project_id: [null],
      road_length_km: [null],
      lrs_route_id: [null],
      lrs_from_km: [null],
      lrs_to_km: [null],
      lrs_chainage: [''],
    });

    this.route.paramMap.subscribe((pm) => {
      const id = pm.get('id');
      if (id && id !== 'new') {
        this.recordId = Number(id);
        this.loadRecord(this.recordId);
      } else {
        this.route.queryParams.subscribe((qp) => {
          if (qp['project_id']) {
            this.form.patchValue({ project_id: Number(qp['project_id']) });
            this.loadLrsRoutes(qp['project_id']);
          }
        });
        this.loadLrsRoutes();
      }
    });
  }

  private loadRecord(id: number): void {
    this.loading = true;
    this.apiService.getRoadBoQById(id).subscribe({
      next: (res: any) => {
        const row = res.data || res;
        const meta = this.parseMetadata(row.metadata);
        const lrs = (meta['lrs'] as Record<string, unknown>) || {};
        this.form.patchValue({
          boq_code: row.boq_code,
          boq_name: row.boq_name,
          project_id: row.project_id,
          road_length_km: row.road_length_km,
          lrs_route_id: lrs['route_id'] ?? row.lrs_route_id ?? null,
          lrs_from_km: lrs['from_km'] ?? row.lrs_from_km ?? null,
          lrs_to_km: lrs['to_km'] ?? row.lrs_to_km ?? null,
          lrs_chainage: lrs['chainage'] || row.lrs_chainage || '',
        });
        this.loadLrsRoutes(row.project_id != null ? String(row.project_id) : undefined);
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.toastr.error(err?.error?.error || 'Failed to load road BoQ');
      },
    });
  }

  private parseMetadata(raw: unknown): Record<string, unknown> {
    if (!raw) return {};
    if (typeof raw === 'object' && !Array.isArray(raw)) return raw as Record<string, unknown>;
    try {
      return JSON.parse(String(raw));
    } catch {
      return {};
    }
  }

  loadLrsRoutes(projectId?: string): void {
    this.lrsService.listRoutes(projectId ? { project_id: projectId } : undefined).subscribe({
      next: (res) => {
        this.lrsRoutes = res.data || [];
      },
      error: () => {
        this.lrsRoutes = [];
      },
    });
  }

  openOnMap(): void {
    const v = this.form.value;
    const queryParams: Record<string, string | number> = { tab: 'lrs' };
    if (v.lrs_route_id) queryParams['route_id'] = v.lrs_route_id;
    if (v.project_id) queryParams['project_id'] = v.project_id;
    void this.router.navigate(['/home/geospatial'], { queryParams });
  }

  generateFromGis(): void {
    const v = this.form.value;
    if (!v.project_id) {
      this.toastr.error('Select a project first');
      return;
    }
    this.dialog.open(GisBoqGenerateDialogComponent, {
      width: '640px',
      maxWidth: '95vw',
      data: {
        project_id: v.project_id,
        route_id: v.lrs_route_id || undefined,
        from_km: v.lrs_from_km || undefined,
        to_km: v.lrs_to_km || undefined,
        domain: 'road',
        target: 'road',
        boq_code: v.boq_code || undefined,
        boq_name: v.boq_name || undefined,
      },
    });
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const v = this.form.value;
    const payload: Record<string, unknown> = {
      boq_code: v.boq_code,
      boq_name: v.boq_name,
      project_id: v.project_id,
      road_length_km: v.road_length_km,
      metadata: {
        lrs: v.lrs_route_id
          ? {
              route_id: v.lrs_route_id,
              from_km: v.lrs_from_km,
              to_km: v.lrs_to_km,
              chainage: v.lrs_chainage || undefined,
            }
          : undefined,
      },
    };

    this.saving = true;
    const req = this.recordId
      ? this.apiService.updateRoadBoQ(this.recordId, payload)
      : this.apiService.createRoadBoQ(payload);

    req.subscribe({
      next: () => {
        this.saving = false;
        this.toastr.success(this.recordId ? 'Road BoQ updated' : 'Road BoQ created');
        void this.router.navigate(['/home/road-boq-list']);
      },
      error: (err) => {
        this.saving = false;
        this.toastr.error(err?.error?.error || 'Save failed');
      },
    });
  }

  backToList(): void {
    void this.router.navigate(['/home/road-boq-list']);
  }
}
